import React from "react";
import { Building, Hash, DollarSign, Calendar, MapPin } from "lucide-react";

const OfficeAdminCard = ({ project, client, isClient, showAddress, onClick }) => {
  const formatJobNumber = () => {
    if (!project?.jobNumber) return "N/A";
    const jobWithRev = project.rev
      ? `${project.jobNumber} Rev ${project.rev}`
      : project.jobNumber;
    return jobWithRev;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return "TBD";
    return `$${price.toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatAddress = (project) => {
    if (!project || !project.clientState) return null;
    return project.clientState;
  };

  // Render client card
  if (isClient && client) {
    return (
      <div
        onClick={onClick}
        style={{
          background: 'var(--dark-elevated)',
          border: '1px solid var(--gray-600)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          cursor: 'pointer',
          transition: 'all var(--transition-fast)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--cta-blue)';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-md)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--gray-600)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: 'var(--space-3)',
            paddingBottom: 'var(--space-3)',
            borderBottom: '1px solid var(--gray-700)',
          }}
        >
          <h3
            style={{
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-50)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: '1.3',
              minHeight: '2.6em',
            }}
            title={client.name}
          >
            {client.name || "Unnamed Client"}
          </h3>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
            <Building
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Remote Service Client
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2)',
              marginTop: 'auto',
              paddingTop: 'var(--space-2)',
            }}
          >
            <DollarSign
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-50)',
                fontWeight: 'var(--font-medium)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatPrice(client.yearly_cost)} / year
            </span>
          </div>

          {client.clients_invoiced_date && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
              <Calendar
                size={14}
                style={{
                  flexShrink: 0,
                  marginTop: '2px',
                  color: 'var(--gray-400)',
                }}
              />
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Last: {formatDate(client.clients_invoiced_date)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render project card (default)
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--dark-elevated)',
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--cta-blue)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-600)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header */}
      <div
        style={{
          marginBottom: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--gray-700)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)',
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.3',
            minHeight: '2.6em',
          }}
          title={project?.projectName}
        >
          {project?.projectName || "Unnamed Project"}
        </h3>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
          <Hash
            size={14}
            style={{
              flexShrink: 0,
              marginTop: '2px',
              color: 'var(--gray-400)',
            }}
          />
          <span
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-300)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {formatJobNumber()}
          </span>
        </div>

        {project?.clientName && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
            <Building
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={project.clientName}
            >
              {project.clientName}
            </span>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            marginTop: 'auto',
            paddingTop: 'var(--space-2)',
          }}
        >
          <DollarSign
            size={14}
            style={{
              flexShrink: 0,
              marginTop: '2px',
              color: 'var(--gray-400)',
            }}
          />
          <span
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-50)',
              fontWeight: 'var(--font-medium)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {formatPrice(project?.modelPrice || project?.price)}
          </span>
        </div>

        {showAddress && formatAddress(project) && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
            <MapPin
              size={14}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={`State: ${formatAddress(project)}`}
            >
              {formatAddress(project)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeAdminCard;
